import { useMutation } from '@apollo/client';
import React from 'react';
import styled from 'styled-components';
import { SvgIcon } from '../../../../components/Icon';
import { theme } from '../../../../styles/theme';
import { EDIT_ALLOWED_DOMAIN_SETTING } from '../../../../utils/apollo/globalConfig';
import {
  IAllowedDomainSettingEditMutation,
  IAllowedDomainSettingEditMutationVariables,
  PermissionKey,
} from '../../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import usePermission from '../../../../utils/hooks/permissionHooks';
import toastMessage from '../../../../utils/toastMessage';
import DefaultButton from '../../../../components/Button/DefaultButton';

const DomainHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
`;

const DomainTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${theme.colors.secondary900};
`;

const DomainList = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 320px;
`;

const DomainItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 0;
  border-top: 1px solid ${theme.colors.secondary300};
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.secondary900};
  padding-left: 16px;
  height: 44px;
`;

const ConnectedDomains = ({
  allowedDomains,
  refetch,
}: {
  allowedDomains: string[];
  refetch: () => void;
}) => {
  const { checkIsPermitted } = usePermission();
  const isPermittedToEdit = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminSettingEdit],
  });

  const [updateAllowedDomainSetting, { loading: isSubmitting }] = useMutation<
    IAllowedDomainSettingEditMutation,
    IAllowedDomainSettingEditMutationVariables
  >(EDIT_ALLOWED_DOMAIN_SETTING);

  const handleRemoveDomain = async (domainToRemove: string) => {
    const updatedDomains = allowedDomains.filter(
      (domain) => domain !== domainToRemove,
    );

    const response = await updateAllowedDomainSetting({
      variables: {
        input: {
          allowedDomains: updatedDomains,
        },
      },
    });

    if (printGraphqlErrors(response?.data?.allowedDomainSettingEdit?.errors)) {
      return;
    }

    refetch();
    toastMessage({ message: 'Domain removed successfully.', type: 'info' });
  };

  return (
    <>
      {allowedDomains.length > 0 && (
        <>
          <DomainHeader>
            <SvgIcon name="domain" height={12} width={12} />
            <DomainTitle>Connected Domains</DomainTitle>
          </DomainHeader>
          <DomainList>
            {allowedDomains.map((domain) => (
              <DomainItem key={domain}>
                {domain}
                {isPermittedToEdit && (
                  <DefaultButton
                    onClick={() => handleRemoveDomain(domain)}
                    disabled={isSubmitting}
                  >
                    Remove
                  </DefaultButton>
                )}
              </DomainItem>
            ))}
          </DomainList>
        </>
      )}
    </>
  );
};

export default ConnectedDomains;
